/** Pages outside `/deliveries` that can open the connection workspace and expect a way back. */
export const WORKSPACE_RETURN_SOURCES = ['product-detail', 'platform-coverage'] as const;

export type WorkspaceReturnSource = (typeof WORKSPACE_RETURN_SOURCES)[number];

/**
 * Where the workspace's back link returns to, carried in its search params.
 * `fromRun` returns to a delivery run; `source='product-detail'` returns to a
 * catalog-operations product (`sourceBatchId` absent → the needs-attention route);
 * `source='platform-coverage'` returns to Platform ▸ Coverage with its filters (`sourceSearch`).
 */
export interface WorkspaceReturnContext {
  fromRun?: string;
  source?: WorkspaceReturnSource;
  sourceProductId?: string;
  sourceBatchId?: string;
  /** Platform ▸ Coverage's query string (`?subsidiaryId=…`), restored on the way back. */
  sourceSearch?: string;
}

/** Non-empty string search value, else `undefined`. */
function nonEmpty(value: unknown): string | undefined {
  return typeof value === 'string' && value !== '' ? value : undefined;
}

/**
 * Validates the return context from raw search params. A product-detail source
 * without a product id is dropped, so the back link never targets a broken route.
 */
export function parseWorkspaceReturn(search: Record<string, unknown>): WorkspaceReturnContext {
  const out: WorkspaceReturnContext = {};
  const fromRun = nonEmpty(search.fromRun);
  if (fromRun) out.fromRun = fromRun;
  const sourceProductId = nonEmpty(search.sourceProductId);
  if (search.source === 'product-detail' && sourceProductId) {
    out.source = 'product-detail';
    out.sourceProductId = sourceProductId;
    const sourceBatchId = nonEmpty(search.sourceBatchId);
    if (sourceBatchId) out.sourceBatchId = sourceBatchId;
  }
  if (search.source === 'platform-coverage') {
    out.source = 'platform-coverage';
    // A query string only: never a path or another origin.
    const sourceSearch = nonEmpty(search.sourceSearch);
    if (sourceSearch?.startsWith('?')) out.sourceSearch = sourceSearch;
  }
  return out;
}

/**
 * Copies only the set return fields — for navigations that rebuild the search
 * from scratch (clearing `sku` / `uploadId`) but must keep the way back.
 */
export function carryWorkspaceReturn(prev: WorkspaceReturnContext): WorkspaceReturnContext {
  return {
    ...(prev.fromRun ? { fromRun: prev.fromRun } : {}),
    ...(prev.source ? { source: prev.source } : {}),
    ...(prev.sourceProductId ? { sourceProductId: prev.sourceProductId } : {}),
    ...(prev.sourceBatchId ? { sourceBatchId: prev.sourceBatchId } : {}),
    ...(prev.sourceSearch ? { sourceSearch: prev.sourceSearch } : {}),
  };
}
